import { type ReactNode, createContext, useCallback, useContext, useState } from "react"
import type { AuthStorage } from "../lib/auth-storage"
import type { AuthAccount, AuthState, AuthUser } from "../types/auth.types"

interface AuthContextValue extends AuthState {
  login: (token: string, user: AuthUser, account?: AuthAccount) => void
  logout: () => void
  isAdmin: boolean
  isSupervisor: boolean
  canWrite: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
  storage: AuthStorage
  onLogout?: () => void
  loginRedirect?: string
}

export function AuthProvider({
  children,
  storage,
  onLogout,
  loginRedirect = "/login",
}: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(() => ({
    token: storage.getToken(),
    user: storage.getUser(),
    account: storage.getAccount(),
    isAuthenticated: !!storage.getToken(),
    isLoading: false,
  }))

  const login = useCallback(
    (token: string, user: AuthUser, account?: AuthAccount) => {
      storage.setToken(token)
      storage.setUser(user)
      if (account) storage.setAccount(account)
      setState({
        token,
        user,
        account: account ?? null,
        isAuthenticated: true,
        isLoading: false,
      })
    },
    [storage],
  )

  const logout = useCallback(() => {
    storage.clearAll()
    setState({
      token: null,
      user: null,
      account: null,
      isAuthenticated: false,
      isLoading: false,
    })
    onLogout?.()
    if (loginRedirect && window.location.pathname !== loginRedirect) {
      window.location.href = loginRedirect
    }
  }, [storage, onLogout, loginRedirect])

  const role = state.user?.role ?? ""
  const isAdmin = role === "admin"
  const isSupervisor = role === "supervisor"
  const canWrite = isAdmin || isSupervisor

  return (
    <AuthContext.Provider value={{ ...state, login, logout, isAdmin, isSupervisor, canWrite }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}
